import React, { useEffect, useRef, useContext } from 'react';
import { googleSignIn } from '../../services/AuthServices';
import AuthContext from '../../context/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import styles from './styles/LoginRegister.module.css'; 

/**
 * Renders a Google Sign-In/Sign-Up button.
 * @param {string} [buttonTextType='signin_with'] - Determines the button text.
 * @param {boolean} [disableOneTap=false] - If true, cancels the automatic One Tap prompt.
 * @param {function} setErrorMessage - State setter for displaying error messages.
 * @param {function} setShowModal - State setter for the auth modal.
 * @param {function} setAcceptInvite - State setter for classroom invites.
 * @param {boolean} acceptInvite - State value for classroom invites.
 */
function GoogleSignInButton({ 
    buttonTextType = 'signin_with', 
    disableOneTap = false,
    setErrorMessage, 
    setShowModal, 
    setAcceptInvite, 
    acceptInvite 
}) {
    const { getLoggedIn } = useContext(AuthContext);
    const navigate = useNavigate();
    const location = useLocation();
    const googleButtonRef = useRef(null);

    const handleGoogleSignIn = async (response) => {
        
        const idToken = response.credential;
        const inviteToken = location.state?.token; 

        try {
            const googleLoginData = { idToken, inviteToken: inviteToken || null };
            await googleSignIn(googleLoginData);

            await getLoggedIn();
            if(setShowModal) setShowModal(false);
            if(setAcceptInvite) setAcceptInvite(false);

            if (acceptInvite && inviteToken && location.state?.classroomId) {
                navigate(`/my-classrooms/${location.state.classroomId}/d`, { replace: true, state: { showModal: false } });
            } else {
                navigate("/home", { replace: true, state: { showModal: false } });
            }
        } catch (error) {
            console.error("Google Sign-In Error:", error);
            if (error.response?.data?.errorMessage) {
                setErrorMessage(error.response.data.errorMessage);
            } else {
                setErrorMessage("An error occurred during Google Sign-In.");
            }
        }
    };

    useEffect(() => {
        let intervalId = null;

        const renderGoogleButton = () => {
            if (!(window.google?.accounts?.id) || !googleButtonRef.current) {
                return false;
            }

            window.google.accounts.id.initialize({
                client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
                callback: handleGoogleSignIn
            });

            window.google.accounts.id.renderButton(
                googleButtonRef.current,
                {
                    theme: "outline",
                    size: "large",
                    type: "standard",
                    shape: "rectangular",
                    text: buttonTextType,
                }
            );

            if (disableOneTap) {
                window.google.accounts.id.cancel();
            }

            return true;
        };

        // Try immediately; if the GIS script hasn't loaded yet, poll until it has.
        if (!renderGoogleButton()) {
            intervalId = setInterval(() => {
                if (renderGoogleButton()) {
                    clearInterval(intervalId);
                    intervalId = null;
                }
            }, 100);
        }

        return () => {
            if (intervalId) clearInterval(intervalId);
        };
    }, [buttonTextType, disableOneTap]);

    return (
        <div ref={googleButtonRef} className={styles.googleButtonContainer}></div>
    );
}

export default GoogleSignInButton;